import { format, isValid, parse } from 'date-fns'
import {
  type CalendarFilterState,
  serializeCalendarFilters,
} from '@opallabs/filters'

/**
 * The month a person can link to, what they have filtered it by, and what they
 * are searching it for: `#month/2026-08?f.label.in=1,2&q=burger`.
 *
 * The calendar lives in the monolith's iframe, so the hash it reads on the way
 * in is its own, and the hash it writes on the way out belongs to the parent
 * frame. Both are the same text, which is what makes the link shareable — and
 * what makes a reload come back to the same filtered month, since the iframe is
 * rebuilt from the parent's address and nothing else survives.
 *
 * The filters are written in the same `f.<category>.<in|ex>` names the query
 * string uses, so one codec answers for both. The keyword is written beside
 * them as `q`, a name that codec does not read.
 */
const MONTH_PATTERN = /^#?month\/(\d{4}-\d{2})(\?.*)?$/
const MONTH_FORMAT = 'yyyy-MM'
const QUICKFIND_PARAM = 'q'

/** The month named by a hash, or `undefined` when it names none. */
export const parseMonthHash = (hash: string): Date | undefined => {
  const matched = hash.match(MONTH_PATTERN)?.[1]
  if (matched === undefined) return undefined

  const parsed = parse(matched, MONTH_FORMAT, new Date())
  return isValid(parsed) ? parsed : undefined
}

/**
 * The filter parameters a hash carries, empty when it carries none. A hash the
 * month view did not write reads as empty rather than as an error.
 */
export const parseFilterHash = (hash: string): URLSearchParams =>
  new URLSearchParams(hash.match(MONTH_PATTERN)?.[2]?.slice(1) ?? '')

/** The keyword a hash carries, or `''` when it carries none. */
export const parseQuickfindHash = (hash: string): string =>
  parseFilterHash(hash).get(QUICKFIND_PARAM)?.trim() ?? ''

/** The route to hand the parent frame so the address bar follows the calendar. */
export const monthHashRoute = (
  month: Date,
  filters?: CalendarFilterState,
  quickfind = ''
): string => {
  const params =
    filters === undefined
      ? new URLSearchParams()
      : serializeCalendarFilters(filters)
  const keyword = quickfind.trim()
  if (keyword !== '') params.set(QUICKFIND_PARAM, keyword)
  const query = params.toString()

  return `#month/${format(month, MONTH_FORMAT)}${query === '' ? '' : `?${query}`}`
}

/**
 * The address the composer comes back to: the workspace page, and the month a
 * person left with everything they had filtered and searched it by.
 *
 * The month lives inside the monolith's iframe, and the composer opens as a
 * layer over the same page. When that layer closes it hands an address to the
 * parent frame — the one the shrouds read out of their own `returnTo` — and
 * without this the parent keeps the bare workspace address, so a reload lands
 * on today rather than on the month the person was reading.
 *
 * A month inside a story is drawn on the story's own page, so the address has
 * to name the story as well. That segment takes the story's v2 primary key —
 * the id its page is addressed by — which only the host knows.
 *
 * `undefined` when the workspace has no slug yet, because half an address
 * sends a person somewhere they did not ask for.
 */
export const monthReturnRoute = (
  workspaceSlug: string | undefined,
  month: Date,
  filters?: CalendarFilterState,
  {
    quickfind,
    storyLegacyId,
  }: { quickfind?: string; storyLegacyId?: string } = {}
): string | undefined => {
  if (workspaceSlug === undefined) return undefined

  const page =
    storyLegacyId === undefined
      ? `/moments/${workspaceSlug}`
      : `/moments/${workspaceSlug}/stories/${storyLegacyId}`

  return `${page}${monthHashRoute(month, filters, quickfind)}`
}
